import { Link, type LinkComponentProps } from "@tanstack/react-router";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Spinner } from "./spinner";

export type ButtonVariant = "primary" | "dark" | "secondary" | "outline" | "ghost" | "danger" | "danger-solid" | "white";
export type ButtonSize = "sm" | "md" | "lg" | "xl";

const variants: Record<ButtonVariant, string> = {
  primary: "bg-primary-500 text-white hover:bg-primary-600",
  // Lime is reserved for CTAs on dark backgrounds and completion states.
  dark: "bg-ink text-lime hover:bg-[#2A2B45]",
  secondary: "bg-neutral text-ink hover:bg-line",
  outline: "bg-white text-ink border border-line-strong hover:border-[#B7BCCB]",
  ghost: "bg-transparent text-primary-500 hover:bg-primary-50",
  danger: "bg-white text-danger-500 border border-danger-line hover:bg-danger-bg",
  "danger-solid": "bg-danger-500 text-white hover:bg-danger",
  white: "bg-white text-ink hover:bg-neutral",
};

const sizes: Record<ButtonSize, string> = {
  sm: "h-8 px-3 rounded-lg text-xs gap-1.5",
  md: "h-9 px-4 rounded-[9px] text-[13px] gap-2",
  lg: "h-10 px-5 rounded-[10px] text-sm gap-2",
  xl: "h-[46px] px-6 rounded-xl text-[15px] gap-2",
};

export function buttonClass(variant: ButtonVariant = "primary", size: ButtonSize = "lg", className?: string) {
  return cn(
    "inline-flex items-center justify-center font-semibold whitespace-nowrap transition-colors",
    "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary-500/25",
    "disabled:bg-neutral disabled:text-disabled disabled:border-transparent disabled:cursor-not-allowed",
    variants[variant],
    sizes[size],
    className,
  );
}

interface ButtonProps extends ComponentProps<"button"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner, keeps the width, ignores further clicks (no double submit). */
  loading?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
}

export function Button({ variant, size, loading, icon, iconRight, className, children, disabled, onClick, type = "button", ...rest }: ButtonProps) {
  return (
    <button
      type={type}
      className={buttonClass(variant, size, cn(loading && "opacity-55 cursor-wait", className))}
      disabled={disabled}
      aria-busy={loading || undefined}
      onClick={loading ? (e) => e.preventDefault() : onClick}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
      {!loading && iconRight}
    </button>
  );
}

type ButtonLinkProps = LinkComponentProps<"a"> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconRight?: ReactNode;
  className?: string;
  children?: ReactNode;
};

export function ButtonLink({ variant, size, icon, iconRight, className, children, ...rest }: ButtonLinkProps) {
  return (
    <Link className={buttonClass(variant, size, className)} {...rest}>
      {icon}
      {children}
      {iconRight}
    </Link>
  );
}

interface IconButtonProps extends ComponentProps<"button"> {
  label: string;
  tone?: "soft" | "outline";
  size?: "sm" | "md";
}

export function IconButton({ label, tone = "outline", size = "md", className, children, type = "button", ...rest }: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex items-center justify-center text-ink-2 transition-colors",
        "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary-500/25",
        size === "md" ? "size-9 rounded-[9px]" : "size-8 rounded-lg",
        tone === "soft" ? "bg-neutral hover:bg-line" : "bg-white border border-line hover:border-line-strong",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
